
import CloseIcon from '@/components/icons/CloseIcon.vue';
import { Detail } from '@/models/app.model';
import { MapServicePoint } from '@/models/map-service.model';
import { AppMapImageData } from '@/models/markers-data.model';
import Vue, { PropType } from 'vue';

export default Vue.extend({
    name: 'Map',
    components: { CloseIcon },
    data() {
        return {
            styles: [] as Array<Record<string, string>>,
            mapSizes: [] as Array<{ width: number; height: number }>,
        };
    },
    props: {
        data: {
            type: Array as PropType<AppMapImageData>,
            required: true,
        },
        points: {
            type: Array as PropType<Array<MapServicePoint>>,
            default: [],
        },
        activePointViewAreaRotateDeg: {
            type: Number,
            default: 0,
        },
        details: {
            type: Array as PropType<Array<Detail>>,
            default: () => [],
        },
        loadedTextures: {
            type: Array as PropType<Array<string>>,
            default: [],
        },
        activeFloor: {
            type: Number,
            default: 0,
        },
        floorsCount: {
            type: Number,
            default: 0,
        },
    },
    methods: {
        mapImageLoadHandler(floor: number) {
            const mapImageRef = (this.$refs.mapImageRef as Array<HTMLImageElement>)[floor];
            if (!mapImageRef) return;
            this.mapSizes[floor].width = mapImageRef.naturalWidth;
            this.mapSizes[floor].height = mapImageRef.naturalHeight;
            this.setStyle(floor);
        },
        getPositionLeft(px: number, floor: number) {
            return `${(px / this.mapSizes[floor].width) * 100}%`;
        },
        getPositionTop(px: number, floor: number) {
            return `${(px / this.mapSizes[floor].height) * 100}%`;
        },
        setStyle(floor: number) {
            this.$set(this.styles, floor, this.getStyle(floor));
        },
        setStyles() {
            for (let i = 0; i < this.floorsCount; i += 1) {
                this.$set(this.styles, i, this.getStyle(i));
            }
        },
        getStyle(floor: number) {
            const APP_PADDING = 36;
            const DETAILS_HEIGHT = 56;
            const DETAILS_MARGIN_TOP_BOTTOM = 16;
            const BUTTON_SIZE = 42;
            let maxMapHeight = window.innerHeight - 2 * APP_PADDING - DETAILS_HEIGHT - 2 * DETAILS_MARGIN_TOP_BOTTOM - BUTTON_SIZE;

            if (window.innerHeight < window.innerWidth && window.innerHeight < 500) {
                maxMapHeight = window.innerHeight - 2 * APP_PADDING - DETAILS_HEIGHT - DETAILS_MARGIN_TOP_BOTTOM;
            }

            const mapSize = this.mapSizes[floor];
            const maxMapHeightExceeded = mapSize.height > maxMapHeight;
            const height = maxMapHeightExceeded ? maxMapHeight : mapSize.height;
            const heightResizeRatio = height / mapSize.height;

            let maxMapWidth = window.innerWidth - 2 * APP_PADDING;
            if (window.innerWidth > 600 + 2 * APP_PADDING) maxMapWidth = 600;
            if (window.innerWidth < 450) maxMapWidth = window.innerWidth - 2 * APP_PADDING * 0.5;
            const maxMapWidthExceeded = mapSize.width > maxMapWidth;
            const width = maxMapWidthExceeded ? maxMapWidth : mapSize.width;
            const widthResizeRatio = width / mapSize.width;

            const resizeRatio = widthResizeRatio > heightResizeRatio
                ? heightResizeRatio : widthResizeRatio;

            const heightInPx = `${mapSize.height * resizeRatio}px`;
            const widthInPx = `${mapSize.width * resizeRatio}px`;

            return {
                height: heightInPx,
                width: widthInPx,
            };
        },
        isRoomLoading(roomName: string) {
            const point = this.points.find((p) => p.roomName === roomName && p.default);
            if (point) return false;
            return !this.loadedTextures.includes(roomName);
        },
        getPointsForFloor(floor: number) {
            return this.points.filter((el) => el.floor === floor);
        },
        emitFloorChange(floor: 'up' | 'down') {
            if (floor === 'up' && this.activeFloor < this.floorsCount - 1) {
                this.$emit('floorChange', this.activeFloor + 1);
            } else if (floor === 'down' && this.activeFloor !== 0) {
                this.$emit('floorChange', this.activeFloor - 1);
            }
        },
        setupMapSizes() {
            for (let i = 0; i < this.floorsCount; i += 1) {
                this.mapSizes.push({
                    width: 0,
                    height: 0,
                });
            }
        },
    },
    created() {
        this.setupMapSizes();
        window.addEventListener('resize', this.setStyles);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.setStyles);
    },
});
